/**
 * Multi-Fleet-Renderer für Spieler-Flotten in der Spielwelt.
 * - Reisende Flotten: 3D Schiffmodelle in Formation + Indicator (wie FleetIndicator im Dev-Modus)
 * - Stationierte Flotten: Flache Shader-Marker auf dem Hex
 * - Routen-Linien auf der Karte (TravelLineRenderer)
 * Daten kommen via 'travel:fleets-updated' Event vom TravelMenu.
 */
import { ShaderProgram } from '../renderer/ShaderProgram';
import type { Renderable } from '../renderer/RenderPipeline';
import type { GameModule } from '../core/GameModule';
import type { Engine } from '../core/Engine';
import type { Camera } from '../renderer/Camera';
import type { EventBus } from '../core/EventBus';
import type { TravelConfig } from '../config/TravelConfig';
import type { ActiveTravel, OtherPlanetShips } from '../ui/TravelMenuState';
import { CameraMode } from '../input/CameraController';
import { axialToWorld } from '../math/HexMath';
import { SHIP_TYPE_DEFS, SHIP_TYPE_COUNT } from '../config/ShipTypes';
import { InstancedShipRenderer, createSharedShipShader } from './InstancedShipRenderer';
import { FleetFormation } from '../systems/FleetFormation';
import { loadGlb } from '../renderer/GlbParser';
import { TravelLineRenderer } from './TravelLineRenderer';
import {
  applyFleetData, tickAnimators, computeFormations, renderStationedMarkers,
  type StationedMarker, type TravelingFleetData,
} from './FleetWorldMarkersHelpers';

import markerVertSrc from '../shaders/fleetMarker.vert.glsl?raw';
import markerFragSrc from '../shaders/fleetMarker.frag.glsl?raw';
import indicatorVertSrc from '../shaders/indicator.vert.glsl?raw';
import indicatorFragSrc from '../shaders/indicator.frag.glsl?raw';

const FLOATS_PER_MAT = 16;
const FORMATION_SPACING = 0.4;
const MAX_INSTANCES_PER_TYPE = 128;
const INDICATOR_Y = 0.9;
const INDICATOR_SIZE = 0.5;
const INDICATOR_COLOR: [number, number, number] = [0.25, 0.55, 1.0];
const CLICK_RADIUS_PX = 40;

export class FleetWorldMarkers implements Renderable, GameModule {
  readonly name = 'FleetWorldMarkers';
  private _gl: WebGL2RenderingContext;
  private _hexRadius: number;
  private _time = 0;
  private _events!: EventBus;
  private _camera: Camera | null = null;
  private _canvas!: HTMLCanvasElement;
  private _cameraMode = CameraMode.Free;

  // FlightView follow proxy — stable object updated each frame with tracked fleet position
  private _followProxy = { centerX: 0, centerY: 0.9, centerZ: 0, active: false };
  private _followTravelId: string | null = null;
  private _exitBtn: HTMLButtonElement | null = null;

  // Ship rendering
  private _shipRenderers: InstancedShipRenderer[];
  private _shipShader: ShaderProgram;
  private _formation: FleetFormation;
  private _modelsLoaded = false;
  private _travelingFleets: TravelingFleetData[] = [];
  private _accum: Float32Array[];
  private _accumCounts: number[];

  // Stationed markers (fleetMarker shader)
  private _markerShader: ShaderProgram;
  private _markerVao: WebGLVertexArrayObject;
  private _stationedMarkers: StationedMarker[] = [];

  // Enemy fleet data
  private _enemyTraveling: TravelingFleetData[] = [];
  private _enemyStationed: StationedMarker[] = [];
  private _enemyPlanetShips: StationedMarker[] = [];

  // Indicator markers for traveling fleets (indicator shader — same as dev FleetIndicator)
  private _indicatorShader: ShaderProgram;
  private _indicatorVao: WebGLVertexArrayObject;

  // Route lines
  private _routeLines = new Map<string, TravelLineRenderer>();
  private _travelConfig: TravelConfig;

  private static readonly COLOR_OWN: [number, number, number] = [0.2, 0.5, 1.0];
  private static readonly COLOR_ENEMY: [number, number, number] = [1.0, 0.3, 0.15];
  private static readonly INDICATOR_COLOR_ENEMY: [number, number, number] = [1.0, 0.35, 0.2];
  private static readonly MARKER_SIZE = 1.4;
  private static readonly MARKER_Y = 0.15;

  /** Expose follow proxy so CameraController can track the focused fleet in FlightView. */
  get followProxy(): { centerX: number; centerY: number; centerZ: number; active: boolean } { return this._followProxy; }

  private _planetKeys: (() => ReadonlySet<number>) | null;

  constructor(gl: WebGL2RenderingContext, hexRadius: number, travelConfig: TravelConfig, planetKeys: (() => ReadonlySet<number>) | null = null) {
    this._gl = gl;
    this._hexRadius = hexRadius;
    this._travelConfig = travelConfig;
    this._planetKeys = planetKeys;

    // Ship renderers
    this._shipShader = createSharedShipShader(gl);
    this._shipRenderers = [];
    this._accum = [];
    this._accumCounts = [];
    for (let i = 0; i < SHIP_TYPE_COUNT; i++) {
      this._shipRenderers.push(new InstancedShipRenderer(gl, SHIP_TYPE_DEFS[i], MAX_INSTANCES_PER_TYPE, this._shipShader));
      this._accum.push(new Float32Array(MAX_INSTANCES_PER_TYPE * FLOATS_PER_MAT));
      this._accumCounts.push(0);
    }
    this._formation = new FleetFormation(FORMATION_SPACING, hexRadius);

    // Stationed marker shader + VAO
    this._markerShader = new ShaderProgram(gl, markerVertSrc, markerFragSrc);
    this._markerVao = this._buildQuad(this._markerShader);

    // Indicator shader + VAO (identical to dev FleetIndicator)
    this._indicatorShader = new ShaderProgram(gl, indicatorVertSrc, indicatorFragSrc);
    this._indicatorVao = this._buildQuad(this._indicatorShader);
  }

  async loadModels(): Promise<void> {
    const loaded = new Map<string, ReturnType<typeof loadGlb>>();
    for (let i = 0; i < SHIP_TYPE_COUNT; i++) {
      const url = SHIP_TYPE_DEFS[i].modelUrl;
      if (!loaded.has(url)) loaded.set(url, loadGlb(url));
    }
    const meshes = new Map<string, Awaited<ReturnType<typeof loadGlb>>>();
    for (const [url, promise] of loaded) meshes.set(url, await promise);
    for (let i = 0; i < SHIP_TYPE_COUNT; i++) {
      const mesh = meshes.get(SHIP_TYPE_DEFS[i].modelUrl);
      if (mesh) await this._shipRenderers[i].loadMesh(mesh);
    }
    this._modelsLoaded = true;
  }

  init(engine: Engine): void {
    this._events = engine.events;
    this._canvas = engine.canvas;
    engine.events.on<{ traveling: ActiveTravel[]; stationed: ActiveTravel[]; otherTraveling?: ActiveTravel[]; otherStationed?: ActiveTravel[]; otherPlanetShips?: OtherPlanetShips[] }>(
      'travel:fleets-updated', (d) => {
        this._onFleetData(d.traveling, d.stationed);
        this._onEnemyFleetData(d.otherTraveling ?? [], d.otherStationed ?? [], d.otherPlanetShips ?? []);
      },
    );
    engine.events.on<{ mode: number }>('camera:mode-changed', (d) => {
      this._cameraMode = d.mode;
      if (this._exitBtn) this._exitBtn.style.display = d.mode === CameraMode.FlightView ? 'block' : 'none';
      if (d.mode !== CameraMode.FlightView) {
        this._followTravelId = null;
        this._followProxy.active = false;
      }
    });
    this._canvas.addEventListener('click', this._onClick);
    this._exitBtn = this._createExitButton();
  }

  update(dt: number): void {
    this._time += dt;
    if (this._travelingFleets.length > 0) {
      tickAnimators(this._travelingFleets, dt);
    }
    if (this._enemyTraveling.length > 0) {
      tickAnimators(this._enemyTraveling, dt);
    }
    // Update follow proxy for FlightView tracking
    if (this._followTravelId) {
      const tracked = this._travelingFleets.find(f => f.travelId === this._followTravelId);
      if (tracked && !tracked.finished) {
        this._followProxy.centerX = tracked.wx;
        this._followProxy.centerZ = tracked.wz;
        this._followProxy.active = true;
      } else {
        // Fleet finished or gone — exit FlightView
        this._followProxy.active = false;
        this._followTravelId = null;
        if (this._cameraMode === CameraMode.FlightView) {
          this._events.emit('fleet:exit-flightview', null);
        }
      }
    }
    for (const line of this._routeLines.values()) line.advanceTime(dt);
    const allTraveling = this._travelingFleets.length + this._enemyTraveling.length;
    if (this._modelsLoaded && allTraveling > 0) {
      const combined = this._enemyTraveling.length > 0
        ? [...this._travelingFleets, ...this._enemyTraveling]
        : this._travelingFleets;
      computeFormations(combined, this._formation, this._accum, this._accumCounts, this._shipRenderers, this._time);
    }
  }

  render(gl: WebGL2RenderingContext, camera: Camera): void {
    this._camera = camera;
    // Route lines
    for (const line of this._routeLines.values()) line.render(gl, camera);
    // 3D ship models (own + enemy combined in computeFormations)
    const allTraveling = this._travelingFleets.length + this._enemyTraveling.length;
    if (this._modelsLoaded && allTraveling > 0) {
      for (let i = 0; i < this._shipRenderers.length; i++) this._shipRenderers[i].render(gl, camera, this._time);
    }
    // Indicator markers over own traveling fleets (blue)
    if (this._travelingFleets.length > 0 && this._cameraMode !== CameraMode.FlightView) {
      this._renderIndicators(gl, camera);
    }
    // Indicator markers over enemy traveling fleets (red/orange)
    if (this._enemyTraveling.length > 0 && this._cameraMode !== CameraMode.FlightView) {
      this._renderEnemyIndicators(gl, camera);
    }
    // Stationed markers — own (blue)
    if (this._stationedMarkers.length > 0) {
      renderStationedMarkers(gl, camera, this._markerShader, this._markerVao, this._stationedMarkers, this._time, FleetWorldMarkers.COLOR_OWN, FleetWorldMarkers.MARKER_SIZE, FleetWorldMarkers.MARKER_Y);
    }
    // Stationed markers — enemy fleet_travels (red/orange)
    if (this._enemyStationed.length > 0) {
      renderStationedMarkers(gl, camera, this._markerShader, this._markerVao, this._enemyStationed, this._time, FleetWorldMarkers.COLOR_ENEMY, FleetWorldMarkers.MARKER_SIZE, FleetWorldMarkers.MARKER_Y);
    }
    // Stationed markers — enemy planet ships (red/orange)
    if (this._enemyPlanetShips.length > 0) {
      renderStationedMarkers(gl, camera, this._markerShader, this._markerVao, this._enemyPlanetShips, this._time, FleetWorldMarkers.COLOR_ENEMY, FleetWorldMarkers.MARKER_SIZE, FleetWorldMarkers.MARKER_Y);
    }
  }

  dispose(): void {
    for (const r of this._shipRenderers) r.dispose();
    this._shipShader.dispose();
    this._gl.deleteVertexArray(this._markerVao);
    this._markerShader.dispose();
    this._gl.deleteVertexArray(this._indicatorVao);
    this._indicatorShader.dispose();
    for (const line of this._routeLines.values()) line.dispose();
    this._routeLines.clear();
    this._canvas.removeEventListener('click', this._onClick);
    if (this._exitBtn) { this._exitBtn.remove(); this._exitBtn = null; }
  }

  // ── Data ─────────────────────────────────────────────

  private _onEnemyFleetData(traveling: ActiveTravel[], stationed: ActiveTravel[], planetShips: OtherPlanetShips[]): void {
    if (traveling.length > 0 || stationed.length > 0) {
      console.log('[FleetWorldMarkers] Enemy data received:', traveling.length, 'traveling,', stationed.length, 'stationed');
    }
    // Enemy traveling fleets — reuse applyFleetData with separate arrays
    const changed = applyFleetData(traveling, stationed, this._hexRadius, this._travelConfig, this._enemyTraveling, this._enemyStationed, this._planetKeys?.() ?? null);
    if (changed) console.log('[FleetWorldMarkers] Enemy markers updated:', this._enemyTraveling.length, 'traveling,', this._enemyStationed.length, 'stationed');
    // Enemy planet ships — convert to stationed markers
    this._enemyPlanetShips.length = 0;
    for (const ps of planetShips) {
      const [wx, wz] = axialToWorld(ps.q, ps.r, this._hexRadius);
      this._enemyPlanetShips.push({ wx, wz });
    }
  }

  private _onFleetData(traveling: ActiveTravel[], stationed: ActiveTravel[]): void {
    const changed = applyFleetData(traveling, stationed, this._hexRadius, this._travelConfig, this._travelingFleets, this._stationedMarkers, this._planetKeys?.() ?? null);
    if (changed) this._reconcileRouteLines();
  }

  // ── Indicator rendering (1:1 from FleetIndicator) ────

  private _renderIndicators(gl: WebGL2RenderingContext, camera: Camera): void {
    this._indicatorShader.use();
    this._indicatorShader.setMat4('u_projection', camera.projection.data);
    this._indicatorShader.setMat4('u_view', camera.view.data);
    this._indicatorShader.setFloat('u_size', INDICATOR_SIZE);
    this._indicatorShader.setVec3('u_color', INDICATOR_COLOR[0], INDICATOR_COLOR[1], INDICATOR_COLOR[2]);
    this._indicatorShader.setFloat('u_time', this._time);

    gl.enable(gl.BLEND);
    gl.blendFunc(gl.SRC_ALPHA, gl.ONE);
    gl.depthMask(false);
    gl.bindVertexArray(this._indicatorVao);

    for (let i = 0; i < this._travelingFleets.length; i++) {
      const f = this._travelingFleets[i];
      if (f.finished) continue;
      this._indicatorShader.setVec3('u_worldPos', f.wx, INDICATOR_Y, f.wz);
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
    }

    gl.bindVertexArray(null);
    gl.depthMask(true);
    gl.disable(gl.BLEND);
  }

  private _renderEnemyIndicators(gl: WebGL2RenderingContext, camera: Camera): void {
    this._indicatorShader.use();
    this._indicatorShader.setMat4('u_projection', camera.projection.data);
    this._indicatorShader.setMat4('u_view', camera.view.data);
    this._indicatorShader.setFloat('u_size', INDICATOR_SIZE);
    this._indicatorShader.setVec3('u_color', FleetWorldMarkers.INDICATOR_COLOR_ENEMY[0], FleetWorldMarkers.INDICATOR_COLOR_ENEMY[1], FleetWorldMarkers.INDICATOR_COLOR_ENEMY[2]);
    this._indicatorShader.setFloat('u_time', this._time);
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.SRC_ALPHA, gl.ONE);
    gl.depthMask(false);
    gl.bindVertexArray(this._indicatorVao);
    for (let i = 0; i < this._enemyTraveling.length; i++) {
      const f = this._enemyTraveling[i];
      if (f.finished) continue;
      this._indicatorShader.setVec3('u_worldPos', f.wx, INDICATOR_Y, f.wz);
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
    }
    gl.bindVertexArray(null);
    gl.depthMask(true);
    gl.disable(gl.BLEND);
  }

  // ── Click → focus camera on fleet (same as FleetIndicator._onClick) ──

  private _onClick = (e: MouseEvent): void => {
    if (!this._camera || this._travelingFleets.length === 0) return;
    if (this._cameraMode === CameraMode.FlightView) return;

    const rect = this._canvas.getBoundingClientRect();
    const sx = e.clientX - rect.left, sy = e.clientY - rect.top;

    let bestDist = CLICK_RADIUS_PX * CLICK_RADIUS_PX;
    let bestFleet: TravelingFleetData | null = null;

    for (const f of this._travelingFleets) {
      if (f.finished) continue;
      const sp = this._projectToScreen(f.wx, INDICATOR_Y, f.wz);
      if (!sp) continue;
      const dx = sx - sp[0], dy = sy - sp[1];
      const d2 = dx * dx + dy * dy;
      if (d2 < bestDist) { bestDist = d2; bestFleet = f; }
    }

    if (bestFleet) {
      // Set follow proxy to this fleet and enter FlightView (same as dev FleetIndicator)
      this._followTravelId = bestFleet.travelId;
      this._followProxy.centerX = bestFleet.wx;
      this._followProxy.centerZ = bestFleet.wz;
      this._followProxy.active = true;
      this._events.emit('fleet:enter-flightview', null);
    }
  };

  /** Project world pos → screen pixel. Returns [sx, sy] or null if behind camera. */
  private _projectToScreen(x: number, y: number, z: number): [number, number] | null {
    const cam = this._camera!;
    const vd = cam.view.data, pd = cam.projection.data;
    const ex = vd[0]*x + vd[4]*y + vd[8]*z  + vd[12];
    const ey = vd[1]*x + vd[5]*y + vd[9]*z  + vd[13];
    const ez = vd[2]*x + vd[6]*y + vd[10]*z + vd[14];
    const ew = vd[3]*x + vd[7]*y + vd[11]*z + vd[15];
    const px = pd[0]*ex + pd[4]*ey + pd[8]*ez  + pd[12]*ew;
    const py = pd[1]*ex + pd[5]*ey + pd[9]*ez  + pd[13]*ew;
    const pw = pd[3]*ex + pd[7]*ey + pd[11]*ez + pd[15]*ew;
    if (pw <= 0) return null;
    const w = this._canvas.clientWidth, h = this._canvas.clientHeight;
    return [(px / pw * 0.5 + 0.5) * w, (-py / pw * 0.5 + 0.5) * h];
  }

  // ── Route lines ──────────────────────────────────────

  private _reconcileRouteLines(): void {
    const activeIds = new Set<string>();
    const cfg = this._travelConfig;
    for (const f of this._travelingFleets) {
      activeIds.add(f.travelId);
      if (f.routeHexes && f.routeBuilt && !this._routeLines.has(f.travelId)) {
        const line = new TravelLineRenderer(this._gl, cfg);
        line.buildFromHexes(
          f.routeHexes, this._hexRadius, cfg.flightHeight,
          f.routeBuilt.segmentKinds, cfg.routeColors.normal, cfg.routeColors.warp,
        );
        this._routeLines.set(f.travelId, line);
      }
    }
    for (const [id, line] of this._routeLines) {
      if (!activeIds.has(id)) { line.dispose(); this._routeLines.delete(id); }
    }
  }

  // ── FlightView exit button ──────────────────────────

  private _createExitButton(): HTMLButtonElement {
    const btn = document.createElement('button');
    btn.textContent = '\u2715 Verlassen';
    Object.assign(btn.style, {
      position: 'fixed', top: '16px', right: '16px', zIndex: '9000',
      display: 'none', padding: '10px 20px',
      background: 'rgba(10,15,30,0.85)', color: '#7ab8ff',
      border: '1px solid rgba(100,160,255,0.4)', borderRadius: '6px',
      fontSize: '14px', fontWeight: 'bold', cursor: 'pointer',
      backdropFilter: 'blur(6px)', letterSpacing: '0.5px',
      boxShadow: '0 2px 12px rgba(30,80,200,0.3)',
    });
    btn.addEventListener('pointerenter', () => { btn.style.background = 'rgba(30,60,120,0.9)'; btn.style.borderColor = 'rgba(120,180,255,0.7)'; });
    btn.addEventListener('pointerleave', () => { btn.style.background = 'rgba(10,15,30,0.85)'; btn.style.borderColor = 'rgba(100,160,255,0.4)'; });
    btn.addEventListener('click', () => { this._events.emit('fleet:exit-flightview', null); });
    document.body.appendChild(btn);
    return btn;
  }

  // ── Helpers ──────────────────────────────────────────

  private _buildQuad(shader: ShaderProgram): WebGLVertexArrayObject {
    const gl = this._gl;
    const vao = gl.createVertexArray()!;
    gl.bindVertexArray(vao);
    const buf = gl.createBuffer()!;
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
    const loc = shader.getAttrib('a_position');
    if (loc >= 0) { gl.enableVertexAttribArray(loc); gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0); }
    gl.bindVertexArray(null);
    gl.bindBuffer(gl.ARRAY_BUFFER, null);
    return vao;
  }
}
